/**
 * NiV tool — system broadcast messages shown to every user as an entry popup
 * (each user dismisses once). Create / list / delete.
 */

import { useState } from "react";
import { useSelector } from "react-redux";
import { ChatCircleDots, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { selectTherapistId } from "../../../../store/slices/authSlice";
import {
  useGetNivSystemMessagesQuery,
  useCreateNivSystemMessageMutation,
  useDeleteNivSystemMessageMutation,
} from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";

const TYPE_LABELS = { info: "מידע", warning: "אזהרה", critical: "קריטי" };

export const formatNivDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function NivSystemMessagesTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const currentTherapistId = useSelector(selectTherapistId);
  const { data: messages = [], isLoading } = useGetNivSystemMessagesQuery(undefined, {
    skip: !isAuthorized,
  });
  const [createMessage, { isLoading: creating }] = useCreateNivSystemMessageMutation();
  const [deleteMessage, { isLoading: deleting }] = useDeleteNivSystemMessageMutation();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState("info");
  const [deleteId, setDeleteId] = useState(null);

  const handleCreate = async () => {
    if (!title.trim() || !body.trim()) {
      showError("יש למלא כותרת ותוכן");
      return;
    }
    try {
      await createMessage({
        title: title.trim(),
        body: body.trim(),
        type,
        createdBy: currentTherapistId,
      }).unwrap();
      showSuccess("ההודעה נוצרה ותוצג למשתמשים");
      setTitle("");
      setBody("");
      setType("info");
    } catch (error) {
      showError(error?.message || "שגיאה ביצירת ההודעה");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMessage({ messageId: deleteId }).unwrap();
      showSuccess("ההודעה נמחקה");
    } catch {
      showError("שגיאה במחיקת ההודעה");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="niv-system-messages-content">
      <div className="niv-system-messages-header">
        <h3>
          <ChatCircleDots size={22} weight="fill" />
          הודעות מערכת למשתמשים
        </h3>
        <span className="niv-system-messages-count">{messages.length} הודעות</span>
      </div>

      <p className="niv-system-messages-hint">
        הודעות אלו מוצגות כחלונית קופצת לכל משתמש בכניסה למערכת. כל משתמש לוחץ "הבנתי" ולא
        יראה את ההודעה שוב.
      </p>

      <div className="niv-system-messages-form">
        <div className="niv-system-messages-form-row">
          <div className="niv-push-form-group">
            <label className="niv-push-label" htmlFor="niv-msg-title">כותרת *</label>
            <input
              id="niv-msg-title"
              type="text"
              className="niv-push-input"
              placeholder='למשל: "תחזוקה מתוכננת"'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="niv-push-form-group niv-system-messages-type-group">
            <label className="niv-push-label" htmlFor="niv-msg-type">סוג</label>
            <select
              id="niv-msg-type"
              className="niv-push-input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="info">מידע</option>
              <option value="warning">אזהרה</option>
              <option value="critical">קריטי</option>
            </select>
          </div>
        </div>
        <div className="niv-push-form-group">
          <label className="niv-push-label" htmlFor="niv-msg-body">תוכן ההודעה *</label>
          <textarea
            id="niv-msg-body"
            className="niv-push-textarea"
            placeholder="תוכן ההודעה שיוצג למשתמשים..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={600}
          />
        </div>
        <button
          type="button"
          className="niv-btn niv-btn-primary niv-push-send-btn"
          onClick={handleCreate}
          disabled={creating || !title.trim() || !body.trim()}
        >
          {creating ? (
            <>
              <LoaderLoop size="xs" /> יוצר...
            </>
          ) : (
            <>
              <PaperPlaneTilt size={18} weight="fill" /> פרסם הודעה
            </>
          )}
        </button>
      </div>

      {isLoading ? (
        <ContentLoader message="טוען הודעות..." />
      ) : messages.length === 0 ? (
        <div className="niv-empty-state">
          <ChatCircleDots size={48} weight="light" />
          <p>אין הודעות מערכת פעילות</p>
        </div>
      ) : (
        <div className="niv-system-updates-list">
          {messages.map((msg) => (
            <div key={msg.id} className="niv-system-update-card">
              <div className="niv-system-update-header">
                <span className="niv-system-update-date">{formatNivDateTime(msg.createdAt)}</span>
                <span className={`niv-system-update-type niv-system-update-type--${msg.type || "info"}`}>
                  {TYPE_LABELS[msg.type] || TYPE_LABELS.info}
                </span>
              </div>
              <div className="niv-system-update-body">
                <p className="niv-system-update-message">
                  <strong>{msg.title}</strong>
                </p>
                <p className="niv-system-update-message">{msg.body}</p>
              </div>
              <button
                type="button"
                className="niv-system-update-delete"
                onClick={() => setDeleteId(msg.id)}
                disabled={deleting}
                title="מחק הודעה"
                aria-label="מחק הודעה"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="מחיקת הודעת מערכת"
        message="ההודעה תוסר ולא תוצג יותר למשתמשים. האם להמשיך?"
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default NivSystemMessagesTool;
